/**
 * The routable notification categories (`docs/idea/notifications.md`). Each value
 * is a `name` handle in `email_routes`. Owner mail resolves its recipients in
 * `resolveAccountMailRecipients` (`packages/db/src/queries/account-mail-recipients.ts`),
 * first hit wins: the verified `email_routes` override named by the category, then
 * `accounts.email`, then every owner / admin member with an address.
 *
 * There is deliberately no "mute": no setting turns a category off, so
 * `escalate.security` cannot be silenced by choice (spec: hardcoded; for an owner
 * or admin the cell is locked, `isLockedChannel`);
 * `LOCKED_CATEGORIES` marks that intent for the UI. The resolver CAN still answer
 * `none` (no route, no `accounts.email`, no owner or admin with an address), and
 * that skip is audited as `email.route_missing`, never silent.
 *
 * `emailCategoryForNotification` is the bridge from the notification fanout's
 * `NotificationKind` vocabulary onto these handles — one routing taxonomy,
 * resolved in one place, whether the mail comes from the agent, the weekly
 * digest, or the fanout.
 */

import { z } from 'zod';
import { ASK_BEFORE_ACTING, ESCALATION_CATEGORY_KIND, SATISFYING } from '../entities/escalation';
import { NotificationKind } from '../entities/notification';

export const NOTIFICATION_CATEGORIES = [
  'escalate.security',
  'escalate.license',
  'escalate.hostile_tone',
  'ask_before_acting.*',
  'satisfying.*',
  'digest.weekly',
] as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);

/** `escalate.security` can never be muted (spec: hardcoded). */
export const LOCKED_CATEGORIES: ReadonlySet<NotificationCategory> = new Set(['escalate.security']);

/** Narrow an arbitrary string (e.g. a URL param) to a known category. */
export function isNotificationCategory(value: string): value is NotificationCategory {
  return (NOTIFICATION_CATEGORIES as readonly string[]).includes(value);
}

/**
 * `NotificationKind` (the fanout's event vocabulary) → the `email_routes`
 * category its EMAIL delivery resolves through — the bridge between the two
 * taxonomies. Without it the dashboard's category matrix governed only the
 * weekly digest and the agent's email-immediate escalations, while the
 * notification fanout emailed each recipient's own address directly: an account
 * that routed `escalate.security` to security@ still got the fanout copy in
 * somebody's personal inbox.
 *
 * | kind | category |
 * |------|----------|
 * | `escalation` / `decision_needed` / `platform_notice` | by the payload's escalation category when it carries one (below), else by kind |
 * | `good_news` | by the payload's escalation category when it carries one (below), else `satisfying.*` |
 * | `task_blocked` | `ask_before_acting.*` — the bot drafted and is waiting |
 * | `pr_merged` | `satisfying.*` |
 * | `ci_failed` / `box_offline` / `share_link` | none |
 *
 * `null` = **no category in the vocab covers this kind**, so the delivery keeps
 * the recipient's own address (the pre-existing behaviour). The six categories
 * are the ones `docs/idea/notifications.md` promises and the dashboard renders;
 * inventing an `operational.*` here would be a routing knob no UI can set.
 */
export function emailCategoryForNotification(input: {
  kind: NotificationKind;
  payload?: Record<string, unknown> | undefined;
}): NotificationCategory | null {
  if (CATEGORY_ROUTED_KINDS.has(input.kind)) {
    // The kind alone can't pick between the `escalate.*` categories —
    // `escalationNotification` stamps the firing category on the payload, so
    // route on that. Unrecognized ⇒ null (account default), never a guess:
    // misrouting a hostile-tone escalation to security@ is worse than
    // delivering it to the maintainer's own inbox. Absent ⇒ the kind's own row.
    const category = input.payload?.['category'];
    if (typeof category === 'string') return escalationRouteFor(category);
  }
  return KIND_ROUTE[input.kind];
}

/**
 * The kinds the category record maps escalation categories to
 * (`ESCALATION_CATEGORY_KIND`) — the only ones whose payload may steer the
 * route. Producers emit only `escalation` of these at this commit;
 * `decision_needed`, `good_news` and `platform_notice` have no producer yet
 * (every escalation still goes out as kind `escalation`; slice 06, #5692,
 * re-points the producers). Read off the record, so a kind a category starts
 * mapping to routes by category without an edit here.
 */
const CATEGORY_ROUTED_KINDS: ReadonlySet<NotificationKind> = new Set(
  Object.values(ESCALATION_CATEGORY_KIND),
);

/** Non-escalation kinds route by kind alone. */
const KIND_ROUTE: Record<NotificationKind, NotificationCategory | null> = {
  [NotificationKind.Escalation]: null, // refined from the payload — see above
  [NotificationKind.TaskBlocked]: 'ask_before_acting.*',
  [NotificationKind.PrMerged]: 'satisfying.*',
  [NotificationKind.CiFailed]: null,
  [NotificationKind.BoxOffline]: null,
  [NotificationKind.ShareLink]: null,
  [NotificationKind.BugScouted]: null,
  // `digest.weekly` is the EMAIL ARCHIVE's category, not the daily ops digest's
  // (#1900): routing this onto it would send the ops note to whoever receives
  // the weekly recap instead of the account's own recipients.
  [NotificationKind.OpsDigest]: null,
  // No category in the vocab covers "the ask you filed is already covered".
  // `ask_before_acting.*` was the tempting one and is wrong: it addresses the
  // people who approve pending BOT actions, and this answer belongs to whoever
  // filed the ask. Null keeps it on the recipient's own address, which is the
  // closest the account-wide fanout can get to the asker today.
  [NotificationKind.AskDuplicate]: null,
  // `satisfying.*` was the tempting one and is wrong in both halves: it is the
  // handle for work that SHIPPED, and it is unsubscribable — routing a
  // disclosure onto it would let an unsubscribe from "nice news" silence the
  // notice that we read someone's tracker. Null keeps it on the account's own
  // recipients.
  [NotificationKind.BacklogImported]: null,
  // `ask_before_acting.*` was the tempting one and is wrong: that handle routes
  // the approvals queue — actions the bot has already drafted and is waiting to
  // take — and an onboarding wait has nothing drafted and nothing to approve.
  // Routing it there would send "your repo is waiting on onboarding" to whoever
  // reviews bot actions rather than to whoever owns the repo, and would let an
  // unsubscribe from approvals silence it. Null keeps it on the account's own
  // recipients.
  [NotificationKind.OnboardingWait]: null,
  // No handle in the vocab covers "your account's key pool cannot serve the
  // held work". `digest.weekly` was the tempting one and is wrong for the
  // reason `ops_digest` above is wrong: that handle addresses whoever
  // receives the weekly recap, not whoever owns the account and its held
  // work. Null keeps it on the account's own recipients, and the dashboard's
  // category matrix is the one place a maintainer mutes it.
  [NotificationKind.KeyOutage]: null,
  // No handle in the vocab covers "you have no model keys at all", and the
  // tempting one is `ask_before_acting.*` because the notice mentions held work
  // — wrong for the reason `onboarding_wait` above is wrong: that handle routes
  // approvals (actions the bot has already drafted), and an account with no key
  // has nothing drafted and nothing to approve. Null keeps it on the account's
  // own recipients, and the dashboard's category matrix is the one place a
  // maintainer mutes it. The kind's own docblock names the four reasons this is
  // its own kind rather than a phase of {@link NotificationKind.KeyOutage}.
  [NotificationKind.NoModelKeys]: null,
  // Without a payload category (a stalled queue, a seat limit, a repo that went
  // private) nothing in the vocab covers it: the account's own recipients.
  [NotificationKind.DecisionNeeded]: null,
  // Every category that maps to this kind is a `SATISFYING` one.
  [NotificationKind.GoodNews]: 'satisfying.*',
  // Hidden tier: in this slice the matrix row is off on every channel and
  // `setPref` refuses an override, so nothing emails it and no route is ever
  // resolved for it. Null keeps the record total.
  [NotificationKind.PlatformNotice]: null,
  // No handle covers "the platform paused coding on your repo"; null keeps the recipient's own address (same reasoning as OnboardingWait above).
  [NotificationKind.CodingPaused]: null,
  // No handle covers "a lane left its pinned model"; the recipient's own address.
  [NotificationKind.LaneDegraded]: null,
  // Never routed by the fan-out: the digest lane mails this kind itself and reads
  // the `digest.weekly` route there (`apps/worker/src/cron/digest.ts`).
  [NotificationKind.WeeklySummary]: null,
};

/**
 * `EscalationCategory` → routable category. The two group-wide arms read the
 * escalation entity's own sets, so a category added to `ASK_BEFORE_ACTING` /
 * `SATISFYING` routes correctly without touching this file. Categories with no
 * matching handle (`cla`, the `OPERATIONAL` faults, the routine deferrals) fall
 * through to null — the vocab has no entry to route them to.
 */
function escalationRouteFor(category: string): NotificationCategory | null {
  switch (category) {
    case 'security':
      return 'escalate.security';
    case 'license':
      return 'escalate.license';
    case 'hostile_tone':
      return 'escalate.hostile_tone';
    default:
      break;
  }
  if ((ASK_BEFORE_ACTING as readonly string[]).includes(category)) return 'ask_before_acting.*';
  if ((SATISFYING as readonly string[]).includes(category)) return 'satisfying.*';
  return null;
}
